// src/pages/Users/Adherent/FicheAdherent.jsx
import React, { useEffect, useState, useMemo } from "react";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import { ToastContainer, toast } from "react-toastify";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import { getAdherentsByUser } from "../../../services/adherentServices";

import AdherentDetails from "./../../PagesAdministrateurs/DetailsAdherent/AdherentDetails";
import AdherentGroupCategory from "./../../PagesAdministrateurs/DetailsAdherent/AdherentGroupCategory";
import AdherentManagers from "./../../PagesAdministrateurs/DetailsAdherent/AdherentManagers";
import AdherentPassageCeinture from "./../../PagesAdministrateurs/DetailsAdherent/AdherentPassageCeinture";
import AdherentPalmares from "./../../PagesAdministrateurs/DetailsAdherent/AdherentPalmares";
import AdherentCompetitions from "./../../PagesAdministrateurs/DetailsAdherent/AdherentCompetitions";
import DossierInscription from "./../../PagesAdministrateurs/DetailsAdherent/DossierInscription";
import ReglementInscription from "./../../PagesAdministrateurs/DetailsAdherent/ReglementInscription";
import AchatsAdherent from "../../PagesAdministrateurs/DetailsAdherent/AchatsAdherent";
import AttendanceList from "../../PagesAdministrateurs/DetailsAdherent/AttendanceList";

import useScrollToTop from "../../../hooks/useScollToTop";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import "react-toastify/dist/ReactToastify.css";
import { FaCalendar } from "react-icons/fa";
import EditAdherentAdminModal from "./../../PagesAdministrateurs/ListingAdherentsAdmin/EditAdherentAdminModal";

/* Avatar par défaut */
const defaultAvatar = (genre) =>
  genre === "Féminin" ? "/img/judokate_avatar.png" : "/img/judoka_avatar.png";

/* Badge statut */
const getStatusPillClass = (statut) => {
  switch (statut) {
    case "Licencié":
      return "bg-green-300 text-slate-800 ring-1 ring-green-300/70";
    case "Licencié Hors Club":
      return "bg-green-200 text-slate-800 ring-1 ring-green-200/70";
    case "Inscrit":
      return "bg-yellow-300 text-slate-900 ring-1 ring-yellow-300/70";
    case "Cours d'essai":
      return "bg-red-300 text-slate-900 ring-1 ring-red-300/70";
    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return "bg-slate-300 text-slate-800 ring-1 ring-slate-300/70";
    case "Ancien Licencié":
      return "bg-slate-900 text-white ring-1 ring-slate-900/50";
    case "Non Licencié - Arrêt":
      return "bg-slate-500 text-white ring-1 ring-slate-500/70";
    default:
      return "bg-slate-200 text-slate-800 ring-1 ring-slate-200/70";
  }
};

function FicheAdherent() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;

  const [adherents, setAdherents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAdherentId, setSelectedAdherentId] = useState(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const list = await getAdherentsByUser(userId);
        const arr = Array.isArray(list) ? list : [];
        setAdherents(arr);
        if (arr.length > 0) setSelectedAdherentId(arr[0].id);
      } catch (e) {
        console.error("Erreur lors de la récupération des adhérents:", e);
        toast.error(
          "Une erreur est survenue lors de la récupération des adhérents."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  useEffect(() => {
    (async () => {
      await refreshAdherents();
    })();
  }, [userId]);

  const selectedAdherent = useMemo(
    () => adherents.find((a) => a.id === selectedAdherentId) || null,
    [adherents, selectedAdherentId]
  );

  const openEditModal = (adh) => {
    const id = typeof adh === "object" ? adh.id : adh;
    if (id) setSelectedAdherentId(id);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const refreshAdherents = async () => {
    try {
      setLoading(true);
      const list = await getAdherentsByUser(userId);
      const arr = Array.isArray(list) ? list : [];
      setAdherents(arr);

      if (!selectedAdherentId) {
        setSelectedAdherentId(arr[0]?.id ?? null);
      } else if (!arr.some((a) => a.id === selectedAdherentId)) {
        setSelectedAdherentId(arr[0]?.id ?? null);
      }
    } catch (e) {
      console.error("Erreur lors du refresh adhérents:", e);
      toast.error("Impossible de rafraîchir la fiche adhérent.");
    } finally {
      setLoading(false);
    }
  };

    const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Utilisateur"
          title="Fiche adhérent"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Bandeau dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Sélectionnez un adhérent pour afficher sa fiche complète.
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Vous ne voyez ici que vos propres adhérents.
              </p>
            </div>
          </div>

          {/* Sélecteur par petites cards */}
          <div className="px-5 py-5 sm:px-8">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className="h-24 rounded-xl bg-white/70 dark:bg-slate-900/40 ring-1 ring-slate-200 dark:ring-slate-800 animate-pulse"
                  />
                ))}
              </div>
            ) : adherents.length === 0 ? (
              <div className="text-center py-10 text-slate-600 dark:text-slate-300">
                Aucun adhérent trouvé.
              </div>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {adherents.map((a) => {
                  const active = a.id === selectedAdherentId;
                  return (
                    <li key={a.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedAdherentId(a.id)}
                        className={`w-full rounded-xl p-3 text-left flex items-center gap-3 transition-all
                          ring-1 shadow-sm
                          ${
                            active
                              ? "bg-white dark:bg-slate-900/50 ring-primary shadow-md"
                              : "bg-white/90 dark:bg-slate-900/40 ring-slate-200 dark:ring-slate-800 hover:ring-primary/60"
                          }`}
                        aria-pressed={active}
                      >
                        <img
                          src={toAbsolute(a.photo_url) || defaultAvatar(a.genre)}
                          alt={`${a.nom || ""} ${a.prenom || ""}`}
                          className="h-14 w-14 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                        />
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {(a.nom || "").toUpperCase()} {a.prenom || ""}
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 ${getStatusPillClass(
                                a.statut_inscription
                              )}`}
                            >
                              {a.statut_inscription || "—"}
                            </span>
                            {a.saison_active && (
                              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-emerald-200 text-emerald-900 ring-1 ring-emerald-300">
                                <FaCalendar /> {a.saison_active}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Contenu détaillé */}
          <div className="px-5 pb-6 sm:px-8">
            {!loading && !selectedAdherent && (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                Sélectionnez un adhérent ci-dessus.
              </div>
            )}

            {selectedAdherent && (
              <div className="grid grid-cols-1 gap-4">
                <div id="sec-profil" className="col-span-1 scroll-mt-[150px]">
                  <AdherentDetails
                    adherent={selectedAdherent}
                    showTeacherNotes={false}
                    onEdit={(selectedAdherent) =>
                      openEditModal(selectedAdherent)
                    }
                  />
                </div>

                <div id="sec-groupes" className="col-span-1 scroll-mt-[150px]">
                  <AdherentGroupCategory adherentId={selectedAdherentId} />
                </div>

                <div id="sec-managers" className="col-span-1 scroll-mt-[150px]">
                  <AdherentManagers adherentId={selectedAdherentId} />
                </div>

                <div
                  id="sec-ceintures"
                  className="col-span-1 scroll-mt-[150px]"
                >
                  <AdherentPassageCeinture adherentId={selectedAdherentId} />
                </div>

                <div
                  id="sec-competitions"
                  className="col-span-1 scroll-mt-[150px]"
                >
                  <AdherentCompetitions adherentId={selectedAdherentId} />
                </div>

                <div id="sec-palmares" className="col-span-1 scroll-mt-[150px]">
                  <AdherentPalmares adherentId={selectedAdherentId} />
                </div>

                <div id="sec-dossier" className="col-span-1 scroll-mt-[150px]">
                  <DossierInscription adherentId={selectedAdherentId} />
                </div>

                <div
                  id="sec-reglement"
                  className="col-span-1 scroll-mt-[150px]"
                >
                  <ReglementInscription
                    adherentId={selectedAdherentId}
                    saison={selectedAdherent.saison_active}
                  />
                </div>

                <div id="sec-achats" className="col-span-1 scroll-mt-[150px]">
                  <AchatsAdherent adherentId={selectedAdherentId} />
                </div>

                <div
                  id="sec-presences"
                  className="col-span-1 scroll-mt-[150px]"
                >
                  <AttendanceList adherentId={selectedAdherentId} />
                </div>
              </div>
            )}
          </div>
        </div>

        {isEditModalOpen && selectedAdherent && (
          <EditAdherentAdminModal
            isOpen={isEditModalOpen}
            onClose={closeEditModal}
            adherent={selectedAdherent}
            refreshList={refreshAdherents}
          />
        )}

        <ToastContainer />
      </div>
    </section>
  );
}

export default FicheAdherent;
